import { useEffect, useState, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  UserPlus, CalendarClock, CalendarDays, Clock, PhoneOff, Users2, Crown,
  BellOff, FileText, TrendingUp, Trophy, XCircle, Gauge, Timer, Zap,
  ArrowUpRight, ChevronRight, AlertTriangle,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { Loading, ErrorView } from "@/components/StateViews";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { fmtINR, fmtDateTime } from "@/lib/format";
import { QualityChip, StageChip } from "@/components/Chips";
import LeadDrawer from "@/components/LeadDrawer";
import DateRangePicker from "@/components/DateRangePicker";

function fmtMins(mins) {
  const m = Math.round(mins || 0);
  if (m <= 0) return "0m";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

const REASON_STYLES = {
  "SLA crossed · not contacted": "bg-red-100 text-red-700",
  "Overdue follow-up": "bg-orange-100 text-orange-700",
  "Not contacted yet": "bg-amber-100 text-amber-700",
  "No next follow-up": "bg-violet-100 text-violet-700",
  "Proposal follow-up due": "bg-sky/70 text-loop",
};

export default function ExecDashboard() {
  const { user } = useAuth();
  const { users } = useOutletContext() || {};
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ created_from: "", created_to: "" });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data: d } = await minDelay(api.get("/dashboard/exec", { params }));
      setData(d.data);
      try { const { data: s } = await api.get("/dashboard/sla", { params }); setSla(s.data); }
      catch { setSla(null); }
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Loading label="Loading your dashboard..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const c = data.cards;
  const today = new Date().toISOString().slice(0, 10);
  const range = {};
  if (filters.created_from) range.created_from = filters.created_from;
  if (filters.created_to) range.created_to = filters.created_to;
  const qs = (obj) => new URLSearchParams(obj).toString();
  const toLeads = (extra) => navigate(`/leads?${qs(extra)}`);
  const toActs = (extra) => navigate(`/activities?${qs(extra)}`);
  const propKey = data.proposal_stage_key || "Proposal Sent";

  const ROWS = [
    {
      title: "Immediate Work", icon: Zap,
      cards: [
        { key: "new_assigned_today", label: "New Assigned Today", today: true, value: c.new_assigned_today, icon: UserPlus, color: "text-emerald-600", bg: "bg-emerald-50", onClick: () => toLeads({ assigned_today: "true", active_scope: "open" }) },
        { key: "due_today", label: "Activities Due Today", today: true, value: c.due_today, icon: CalendarClock, color: "text-loop", bg: "bg-sky/50", onClick: () => toActs({ date_from: today, date_to: today }) },
        { key: "meetings_today", label: "Meetings Today", today: true, value: c.meetings_today, icon: CalendarDays, color: "text-violet-600", bg: "bg-violet-50", onClick: () => toActs({ activity_type: "Meeting", date_from: today, date_to: today }) },
        { key: "overdue_activities", label: "Overdue Activities", value: c.overdue_activities, icon: Clock, color: "text-red-500", bg: "bg-red-50", onClick: () => toActs({ overdue_only: "1" }) },
        { key: "not_contacted", label: "Not Contacted Yet", value: c.not_contacted, icon: PhoneOff, color: "text-orange-500", bg: "bg-orange-50", onClick: () => toLeads({ contacted: "no", active_scope: "open", ...range }) },
      ],
    },
    {
      title: "Active Pipeline", icon: Users2,
      cards: [
        { key: "my_active", label: "My Active Leads", value: c.my_active, icon: Users2, color: "text-loop", bg: "bg-sky/50", onClick: () => toLeads({ active_scope: "open", ...range }) },
        { key: "ace_king", label: "Ace + King Leads", value: c.ace_king, icon: Crown, color: "text-amber-500", bg: "bg-amber-50", onClick: () => toLeads({ quality: "Ace,King", active_scope: "open", ...range }) },
        { key: "no_next_followup", label: "No Next Follow-up", value: c.no_next_followup, icon: BellOff, color: "text-violet-600", bg: "bg-violet-50", onClick: () => toLeads({ no_next_followup: "true", active_scope: "open", ...range }) },
        { key: "proposal_sent", label: "Proposal Sent", value: c.proposal_sent, icon: FileText, color: "text-navy", bg: "bg-mist", onClick: () => toLeads({ stage: propKey, ...range }) },
        { key: "ace_king_advance", label: "Ace + King Pipeline Expected", sub: "Advance amount only", currency: true, value: c.ace_king_advance, icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50", onClick: () => toLeads({ quality: "Ace,King", active_scope: "open", has_deal: "yes", ...range }) },
      ],
    },
    {
      title: "Result + SLA", icon: Gauge,
      cards: [
        { key: "won", label: "Won", value: c.won, icon: Trophy, color: "text-emerald-600", bg: "bg-emerald-50", onClick: () => toLeads({ stage: "Won", ...range }) },
        { key: "lost", label: "Lost", value: c.lost, icon: XCircle, color: "text-slate-400", bg: "bg-slate-100", onClick: () => toLeads({ stage: "Lost", ...range }) },
        { key: "sla_breach_rate", label: "SLA Breach Rate", value: sla ? `${sla.summary.breach_rate}%` : "—", icon: Gauge, color: "text-red-500", bg: "bg-red-50", onClick: () => toLeads({ sla: "breached", status_filter: "all", ...range }) },
        { key: "crossed_sla", label: "Crossed SLA", value: sla ? sla.summary.breached_count : "—", icon: AlertTriangle, color: "text-orange-500", bg: "bg-orange-50", onClick: () => toLeads({ sla: "breached", status_filter: "all", ...range }) },
        { key: "avg_first_response", label: "Avg First Response", value: sla ? fmtMins(sla.summary.avg_time_to_first_response_mins) : "—", icon: Timer, color: "text-loop", bg: "bg-sky/50", onClick: () => toLeads({ contacted: "yes", status_filter: "all", ...range }) },
      ],
    },
  ];

  return (
    <div className="space-y-6" data-testid="exec-dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-sora text-3xl font-bold text-navy">Dashboard</h1>
          <p className="flex items-center gap-2 text-sm text-ink/60">
            Welcome back, {user?.name?.split(" ")[0]} · here's what needs your attention.
            {loading && data && <span data-testid="exec-refreshing" className="flex items-center gap-1 text-loop"><Loader2 className="h-3.5 w-3.5 animate-spin" />Refreshing…</span>}
          </p>
        </div>
        <DateRangePicker
          from={filters.created_from}
          to={filters.created_to}
          onChange={({ created_from, created_to }) => setFilters({ created_from, created_to })}
          testid="exec-date-range"
        />
      </div>

      <div className={`space-y-5 transition-opacity ${loading && data ? "opacity-50" : ""}`}>
        {ROWS.map((row) => (
          <div key={row.title} data-testid={`exec-row-${row.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="mb-2.5 flex items-center gap-2">
              <row.icon className="h-4 w-4 text-loop" />
              <h2 className="font-sora text-sm font-semibold text-navy">{row.title}</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {row.cards.map((cd) => (
                <button
                  key={cd.key}
                  data-testid={`exec-card-${cd.key}`}
                  onClick={cd.onClick}
                  className="group relative rounded-2xl border border-sky/70 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-loop/60 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-loop/40"
                >
                  <ArrowUpRight className="absolute right-3 top-3 h-4 w-4 text-ink/20 opacity-0 transition-opacity group-hover:opacity-100" />
                  <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-xl ${cd.bg} ${cd.color}`}>
                    <cd.icon className="h-[18px] w-[18px]" />
                  </div>
                  <p className={`font-sora font-bold text-navy ${cd.currency ? "text-xl" : "text-2xl"}`}>
                    {cd.currency ? fmtINR(cd.value ?? 0) : (cd.value ?? 0)}
                  </p>
                  <p className="text-xs text-ink/60">{cd.label}</p>
                  {cd.sub && <p className="mt-0.5 text-[10px] text-ink/40">{cd.sub}</p>}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* My Priority Queue */}
      <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="priority-queue">
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-loop/10 text-loop"><Zap className="h-[18px] w-[18px]" /></span>
            <div>
              <h3 className="font-sora text-sm font-semibold text-navy">My Priority Queue</h3>
              <p className="text-xs text-ink/50">The leads most in need of action right now.</p>
            </div>
          </div>
          {data.priority_total > 10 && (
            <button data-testid="priority-view-all" onClick={() => toLeads({ active_scope: "open" })} className="flex items-center gap-1 text-xs font-semibold text-loop hover:underline">
              View all ({data.priority_total}) <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {data.priority_queue.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-sky py-10 text-center" data-testid="priority-empty">
            <Trophy className="mb-2 h-7 w-7 text-emerald-500" />
            <p className="text-sm font-medium text-navy">No priority leads right now.</p>
            <p className="text-xs text-ink/50">You're all caught up — nice work.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-sky/60 text-left text-xs text-ink/50">
                  <th className="px-3 py-2">Lead</th>
                  <th className="px-3 py-2">Quality</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Next / Due</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sky/40">
                {data.priority_queue.map((r) => (
                  <tr
                    key={r.lead_id}
                    data-testid={`priority-row-${r.lead_id}`}
                    onClick={() => setSelected(r.lead_id)}
                    className="cursor-pointer transition-colors hover:bg-sky/20"
                  >
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-navy">{r.name}</p>
                      {r.business_name && <p className="text-[11px] text-ink/50">{r.business_name}</p>}
                    </td>
                    <td className="px-3 py-2.5"><QualityChip value={r.quality} /></td>
                    <td className="px-3 py-2.5">
                      <StageChip value={r.stage_name || r.stage} />
                      {r.sub_stage && <p className="mt-0.5 text-[10px] text-ink/45">{String(r.sub_stage).split(":").pop()}</p>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${REASON_STYLES[r.reason] || "bg-mist text-ink/70"}`}>{r.reason}</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-ink/60">{r.due_at ? fmtDateTime(r.due_at) : "—"}</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-loop">Open <ArrowUpRight className="h-3.5 w-3.5" /></span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LeadDrawer leadId={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} onUpdated={load} users={users} />
    </div>
  );
}
